import { createPortal } from 'react-dom';
import { ImageIcon, Camera, X } from 'lucide-react';

interface AvatarPhotoPickerDialogProps {
  isOpen: boolean;
  onChooseFromDevice: () => void;
  onTakePicture: () => void;
  onCancel: () => void;
}

export default function AvatarPhotoPickerDialog({
  isOpen,
  onChooseFromDevice,
  onTakePicture,
  onCancel,
}: AvatarPhotoPickerDialogProps) {
  if (!isOpen) return null;

  const handleChoose = () => {
    onChooseFromDevice();
    onCancel();
  };

  const handleTake = () => {
    onTakePicture();
    onCancel();
  };

  const dialogContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden
      />
      <div
        className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Profile photo</h3>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <button
            type="button"
            onClick={handleChoose}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-herbal-green/50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-full bg-herbal-green/10 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-herbal-green" />
            </div>
            <div>
              <span className="font-medium text-gray-900">
                Choose from device
              </span>
              <p className="text-sm text-gray-500">
                Upload from gallery or files
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={handleTake}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-herbal-green/50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-full bg-herbal-green/10 flex items-center justify-center">
              <Camera className="w-5 h-5 text-herbal-green" />
            </div>
            <div>
              <span className="font-medium text-gray-900">Take picture</span>
              <p className="text-sm text-gray-500">
                Use camera (web or mobile)
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(dialogContent, document.body)
    : null;
}
